/**
 * Storage key for the portal short code. Duplicated in the portal lib's reader
 * (libs/portal/src/lib/data/short-code.ts) because the app cannot statically import the
 * lazily-loaded lib without defeating the lazy loading, the same trade-off
 * `__portalBffBaseUrl__` already makes. Change both together.
 */
const SHORT_CODE_STORAGE_KEY = 'portalShortCode';

/** Query parameter the magic link carries the short code in (deferred ADR-0010). */
const SHORT_CODE_QUERY_PARAM = 'c';

/**
 * Captures the portal credential from the magic link and persists it.
 *
 * Runs before `bootstrapApplication`, not inside the portal lib, for two reasons: the
 * parameter has to be stripped before the router sees the URL (it would otherwise be
 * dropped by the redirect to /home before the lazy lib ever loads), and stripping keeps a
 * long-lived credential out of history, bookmarks and referrers. A new code overwrites the
 * stored one, so a fresh link always wins.
 *
 * Storage can throw (private browsing, blocked site data) and a portal that cannot store a
 * credential still renders its "link expired or invalid" state honestly, so failures are
 * swallowed rather than breaking bootstrap.
 */
export function captureShortCode(): void {
  const url = new URL(window.location.href);
  const code = url.searchParams.get(SHORT_CODE_QUERY_PARAM);
  if (code === null || code.length === 0) {
    return;
  }

  try {
    localStorage.setItem(SHORT_CODE_STORAGE_KEY, code);
  } catch {
    // Nothing to do: the screens handle a missing credential.
  }

  url.searchParams.delete(SHORT_CODE_QUERY_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}
